<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  show: { type: Boolean, required: true },
  stage: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['close', 'submit', 'delete']);

// Palette of 12 curated colors + an 'outra cor' option that opens a hex input.
const PALETTE = [
  '#94a3b8', // slate (default Novo lead)
  '#3b82f6', // blue (default Em contato)
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#1A9C60', // Helevor green (default Ganho)
  '#84cc16', // lime
  '#eab308', // yellow
  '#f59e0b', // amber
  '#f97316', // orange
  '#ef4444', // red (default Perdido)
  '#ec4899', // pink
  '#a855f7', // purple (default Proposta)
];

const form = ref({
  name: '',
  color: '',
  sla_days: '',
  wip_limit: '',
  is_closed: false,
});
const customColorOpen = ref(false);
const customHex = ref('');

function loadFromProps() {
  const s = props.stage || {};
  form.value = {
    name: s.name || '',
    color: s.color || '',
    sla_days: s.sla_days ?? '',
    wip_limit: s.wip_limit ?? '',
    is_closed: !!s.is_closed,
  };
  if (s.color && !PALETTE.includes(s.color)) {
    customColorOpen.value = true;
    customHex.value = s.color;
  } else {
    customColorOpen.value = false;
    customHex.value = '';
  }
}

watch(() => props.show, val => { if (val) loadFromProps(); });
watch(() => props.stage, () => { if (props.show) loadFromProps(); }, { deep: true });

function selectPaletteColor(hex) {
  form.value.color = hex;
  customColorOpen.value = false;
  customHex.value = '';
}

function openCustomColor() {
  customColorOpen.value = true;
  if (form.value.color && !PALETTE.includes(form.value.color)) {
    customHex.value = form.value.color;
  } else if (!customHex.value) {
    customHex.value = '#';
  }
}

function applyCustomHex() {
  const hex = customHex.value.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
  form.value.color = hex;
}

function submit() {
  if (!form.value.name.trim()) return;
  // Normalize: empty string -> null so backend stores NULL instead of "".
  const payload = {
    name: form.value.name.trim(),
    color: form.value.color || null,
    sla_days: form.value.sla_days === '' ? null : Number(form.value.sla_days),
    wip_limit: form.value.wip_limit === '' ? null : Number(form.value.wip_limit),
    is_closed: !!form.value.is_closed,
  };
  emit('submit', payload);
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-n-solid-1 rounded-xl border border-n-weak w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ stage?.id ? 'Editar etapa' : 'Nova etapa' }}
      </h2>

      <div class="space-y-1">
        <label class="text-xs text-n-slate-11">Nome *</label>
        <input
          v-model="form.name"
          maxlength="80"
          class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-n-slate-12"
          placeholder="Ex.: Negociação"
          @keyup.enter="submit"
        />
      </div>

      <div class="space-y-2">
        <label class="text-xs text-n-slate-11">Cor</label>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="hex in PALETTE"
            :key="hex"
            type="button"
            class="size-6 rounded-md border-2 transition-transform hover:scale-110"
            :class="form.color === hex ? 'border-n-slate-12 ring-2 ring-n-brand/30' : 'border-transparent'"
            :style="{ backgroundColor: hex }"
            :title="hex"
            @click="selectPaletteColor(hex)"
          />
          <button
            type="button"
            class="size-6 rounded-md border-2 grid place-content-center text-xs text-n-slate-11 hover:bg-n-alpha-1"
            :class="customColorOpen ? 'border-n-slate-12 ring-2 ring-n-brand/30' : 'border-dashed border-n-weak'"
            title="Outra cor (hex)"
            @click="openCustomColor"
          >
            +
          </button>
        </div>
        <div v-if="customColorOpen" class="flex items-center gap-2 pt-1">
          <input
            v-model="customHex"
            maxlength="7"
            placeholder="#RRGGBB"
            class="w-28 px-2 py-1 rounded border border-n-weak bg-transparent text-xs text-n-slate-12 font-mono"
            @input="applyCustomHex"
          />
          <span
            v-if="form.color"
            class="size-5 rounded border border-n-weak"
            :style="{ backgroundColor: form.color }"
          />
          <span class="text-xs text-n-slate-10">{{ form.color || 'sem cor' }}</span>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="space-y-1">
          <label class="text-xs text-n-slate-11">SLA (dias)</label>
          <input
            v-model.number="form.sla_days"
            type="number"
            min="1"
            class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-n-slate-12"
            placeholder="—"
          />
          <p class="text-[10px] text-n-slate-10">Prazo automático após entrar na etapa</p>
        </div>
        <div class="space-y-1">
          <label class="text-xs text-n-slate-11">Limite WIP</label>
          <input
            v-model.number="form.wip_limit"
            type="number"
            min="1"
            class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-n-slate-12"
            placeholder="—"
          />
          <p class="text-[10px] text-n-slate-10">Quantos cards podem estar aqui</p>
        </div>
      </div>

      <label class="flex items-start gap-2 cursor-pointer">
        <input
          v-model="form.is_closed"
          type="checkbox"
          class="mt-0.5"
        />
        <span>
          <span class="block text-sm text-n-slate-12">Etapa final</span>
          <span class="block text-[10px] text-n-slate-10">
            Marcar como Ganho/Perdido — cards aqui contam como fechados
          </span>
        </span>
      </label>

      <div class="flex justify-between items-center pt-2 border-t border-n-weak">
        <button
          v-if="stage?.id"
          class="px-3 py-1.5 rounded text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
          @click="$emit('delete', stage)"
        >
          Excluir etapa
        </button>
        <div class="flex gap-2 ml-auto">
          <button
            class="px-3 py-1.5 rounded border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-1"
            @click="$emit('close')"
          >
            Cancelar
          </button>
          <button
            class="px-3 py-1.5 rounded bg-n-brand text-white text-sm disabled:opacity-50"
            :disabled="!form.name.trim()"
            @click="submit"
          >
            {{ stage?.id ? 'Salvar' : 'Criar etapa' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
